import moment from "moment-timezone";

const DEFAULT_TIMEZONE = "Etc/UTC";
const DEFAULT_LOCALE = "en-US";

type DateInput = Date | string | number;

const resolveTimezone = (timezone?: string): string =>
  timezone && moment.tz.zone(timezone) ? timezone : DEFAULT_TIMEZONE;

const parseDateInput = (date: DateInput) => {
  if (typeof date === "string") {
    return moment(date, moment.ISO_8601, true);
  }

  return moment(date);
};

const OFFSET_TIMEZONE_PATTERN = /^[+-]\d{1,2}(?::?\d{2})?$/;
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const getCalendarDateKey = (date: DateInput, timezone: string): string => {
  if (
    typeof date === "string" &&
    DATE_KEY_PATTERN.test(date) &&
    moment(date, "YYYY-MM-DD", true).isValid()
  ) {
    return date;
  }

  return formatStoreDateKey(date, timezone);
};

const parseDateInTimezone = (date: DateInput, timezone: string) => {
  if (
    typeof date === "string" &&
    DATE_KEY_PATTERN.test(date) &&
    moment(date, "YYYY-MM-DD", true).isValid()
  ) {
    return moment.tz(date, "YYYY-MM-DD", true, resolveTimezone(timezone));
  }

  return parseDateInput(date).tz(resolveTimezone(timezone));
};

const getDisplayTimezone = (date: DateInput, timezone?: string): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "";
  }

  const resolvedTimezone = resolveTimezone(timezone);
  const timezoneAbbreviation = parsedDate.tz(resolvedTimezone).format("z");

  if (timezoneAbbreviation && !OFFSET_TIMEZONE_PATTERN.test(timezoneAbbreviation)) {
    return timezoneAbbreviation;
  }

  return resolvedTimezone;
};

export const getBrowserTimezone = (): string => {
  const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  return resolveTimezone(browserTimezone);
};

export const formatStoreDate = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  },
): string => {
  const resolvedTimezone = resolveTimezone(timezone);
  const parsedDate = parseDateInTimezone(date, resolvedTimezone);

  if (!parsedDate.isValid()) {
    return "--";
  }

  return new Intl.DateTimeFormat(locale, {
    ...options,
    timeZone: resolvedTimezone,
  }).format(parsedDate.toDate());
};

export const formatStoreDateKey = (date: DateInput, timezone: string): string => {
  const parsedDate = parseDateInTimezone(date, timezone);

  if (!parsedDate.isValid()) {
    return "";
  }

  return parsedDate.format("YYYY-MM-DD");
};

/** Returns the inclusive count of store-calendar days in a date range. */
export const getInclusiveStoreDayCount = (
  startDate: DateInput,
  endDate: DateInput,
  timezone: string,
): number => {
  const start = parseDateInTimezone(startDate, timezone).startOf("day");
  const end = parseDateInTimezone(endDate, timezone).startOf("day");

  if (!start.isValid() || !end.isValid()) {
    return 1;
  }

  return Math.max(1, end.diff(start, "days") + 1);
};

export const getStoreDayStartUtcTimestamp = (
  date: DateInput,
  timezone: string,
  calendarTimezone: string = timezone,
): string => {
  const dateKey = getCalendarDateKey(date, calendarTimezone);

  if (!dateKey) {
    return "";
  }

  return moment
    .tz(dateKey, "YYYY-MM-DD", true, resolveTimezone(timezone))
    .startOf("day")
    .utc()
    .toISOString();
};

export const formatStoreDateTimeFileStamp = (date: DateInput, timezone: string): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "invalid_date";
  }

  return parsedDate.tz(resolveTimezone(timezone)).format("YYYYMMDD_HHmmss");
};

export const parseStoreDateKey = (
  dateKey?: string | null,
  timezone: string = DEFAULT_TIMEZONE,
): Date | null => {
  if (!dateKey) {
    return null;
  }

  const parsedDate = moment.tz(dateKey, "YYYY-MM-DD", true, resolveTimezone(timezone));

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.toDate();
};

export const formatStoreDateRangeShort = (
  startDate: string,
  endDate: string,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
): { startDate: string; endDate: string } =>
  formatStoreDateRange(
    parseStoreDateKey(startDate, timezone) ?? startDate,
    parseStoreDateKey(endDate, timezone) ?? endDate,
    timezone,
    locale,
    options,
  );

/**
 * Formats range endpoints consistently. A year is included on both endpoints
 * when the range spans calendar years in the displayed timezone.
 */
export const doesStoreDateRangeSpanYears = (
  startDate: DateInput,
  endDate: DateInput,
  timezone: string,
): boolean => {
  const startYear = formatStoreDateKey(startDate, timezone).slice(0, 4);
  const endYear = formatStoreDateKey(endDate, timezone).slice(0, 4);

  return Boolean(startYear && endYear && startYear !== endYear);
};

/**
 * Returns whether a displayed range needs year context relative to the current
 * calendar year in the displayed timezone.
 */
export const doesStoreDateRangeRequireYear = (
  startDate: DateInput,
  endDate: DateInput,
  timezone: string,
  currentDate: DateInput = new Date(),
): boolean => {
  const startYear = formatStoreDateKey(startDate, timezone).slice(0, 4);
  const endYear = formatStoreDateKey(endDate, timezone).slice(0, 4);
  const currentYear = formatStoreDateKey(currentDate, timezone).slice(0, 4);

  return Boolean(
    startYear &&
    endYear &&
    currentYear &&
    (startYear !== endYear || startYear !== currentYear || endYear !== currentYear),
  );
};

/**
 * Decides year visibility for a current/comparison pair. Years are either
 * shown on both ranges or omitted from both ranges.
 */
export const shouldShowStoreDateRangeComparisonYears = (
  currentRange: { start: DateInput; end: DateInput },
  comparisonRange: { start: DateInput; end: DateInput } | undefined,
  timezone: string,
  currentDate: DateInput = new Date(),
): boolean =>
  doesStoreDateRangeRequireYear(currentRange.start, currentRange.end, timezone, currentDate) ||
  Boolean(
    comparisonRange &&
    doesStoreDateRangeRequireYear(
      comparisonRange.start,
      comparisonRange.end,
      timezone,
      currentDate,
    ),
  );

export const formatStoreDateRange = (
  startDate: DateInput,
  endDate: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
  options: Intl.DateTimeFormatOptions = { month: "short", day: "numeric" },
): { startDate: string; endDate: string } => {
  const rangeOptions: Intl.DateTimeFormatOptions = doesStoreDateRangeSpanYears(
    startDate,
    endDate,
    timezone,
  )
    ? { ...options, year: "numeric" }
    : options;

  return {
    startDate: formatStoreDate(startDate, timezone, locale, rangeOptions),
    endDate: formatStoreDate(endDate, timezone, locale, rangeOptions),
  };
};

/**
 * Formats date range endpoints for the reporting-header current/comparison
 * presentation. When years are enabled, same-year ranges place the year only
 * on the end date while calendar-year crossings identify both endpoints.
 */
export const formatStoreDateRangeForComparison = (
  startDate: DateInput,
  endDate: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
  showYear: boolean = false,
): { startDate: string; endDate: string } => {
  const dateOptions: Intl.DateTimeFormatOptions = {
    weekday: "short",
    month: "short",
    day: "numeric",
  };
  const isSingleDay =
    formatStoreDateKey(startDate, timezone) === formatStoreDateKey(endDate, timezone);
  const showYearOnBothEndpoints =
    showYear && (isSingleDay || doesStoreDateRangeSpanYears(startDate, endDate, timezone));

  return {
    startDate: formatStoreDate(
      startDate,
      timezone,
      locale,
      showYearOnBothEndpoints ? { ...dateOptions, year: "numeric" } : dateOptions,
    ),
    endDate: formatStoreDate(
      endDate,
      timezone,
      locale,
      showYear ? { ...dateOptions, year: "numeric" } : dateOptions,
    ),
  };
};

export const getStartOfStoreDay = (date: DateInput, timezone: string): Date =>
  parseDateInput(date).tz(resolveTimezone(timezone)).startOf("day").toDate();

export const getEndOfStoreDay = (date: DateInput, timezone: string): Date =>
  parseDateInput(date).tz(resolveTimezone(timezone)).endOf("day").toDate();

export const getStoreDayIndex = (date: DateInput, timezone?: string): number | null => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return null;
  }

  return parsedDate.tz(resolveTimezone(timezone)).day();
};

export const formatStoreDateTimeTwoLine = (
  date: DateInput,
  timezone?: string,
  locale: string = DEFAULT_LOCALE,
): { line1: string; line2: string; timezoneLabel: string } => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return { line1: "--", line2: "--", timezoneLabel: "" };
  }

  const resolvedTimezone = resolveTimezone(timezone);
  const zonedDate = parsedDate.tz(resolvedTimezone).toDate();
  const timezoneLabel = getDisplayTimezone(date, resolvedTimezone);
  const line1Base = new Intl.DateTimeFormat(locale, {
    timeZone: resolvedTimezone,
    weekday: "short",
    hour: "numeric",
    minute: "2-digit",
  }).format(zonedDate);

  return {
    line1: line1Base,
    line2: new Intl.DateTimeFormat(locale, {
      timeZone: resolvedTimezone,
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(zonedDate),
    timezoneLabel,
  };
};

export const formatStoreDateTimeCsv = (date: DateInput, timezone?: string): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "--";
  }

  return parsedDate.tz(resolveTimezone(timezone)).format("YYYY-MM-DD HH:mm:ss");
};

const formatStoreDateTime = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "--";
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveTimezone(timezone),
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(parsedDate.tz(resolveTimezone(timezone)).toDate());
};

export const formatStoreDateTimeWithTimezone = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string => {
  const formattedDateTime = formatStoreDateTime(date, timezone, locale);

  if (formattedDateTime === "--") {
    return formattedDateTime;
  }

  const timezoneLabel = getDisplayTimezone(date, timezone);
  return timezoneLabel ? `${formattedDateTime} ${timezoneLabel}` : formattedDateTime;
};

export const formatStoreTime = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "--";
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone: resolveTimezone(timezone),
    hour: "numeric",
    minute: "2-digit",
  }).format(parsedDate.tz(resolveTimezone(timezone)).toDate());
};

export const formatStoreTimeWithTimezoneParts = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): { time: string; timezoneLabel: string } => {
  const time = formatStoreTime(date, timezone, locale);

  if (time === "--") {
    return { time, timezoneLabel: "" };
  }

  const timezoneLabel = getDisplayTimezone(date, timezone);
  return { time, timezoneLabel };
};

export const formatStoreTimeWithTimezone = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string => {
  const { time, timezoneLabel } = formatStoreTimeWithTimezoneParts(date, timezone, locale);
  return timezoneLabel ? `${time} ${timezoneLabel}` : time;
};

export const formatRelativeStoreTime = (
  date: DateInput,
  timezone: string,
  locale: string = DEFAULT_LOCALE,
): string => {
  const parsedDate = parseDateInput(date);

  if (!parsedDate.isValid()) {
    return "--";
  }

  const elapsedSeconds = parsedDate.tz(resolveTimezone(timezone)).diff(moment(), "seconds");
  const relativeTimeFormatter = new Intl.RelativeTimeFormat(locale, { numeric: "always" });
  const units: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
    { unit: "year", seconds: 31_536_000 },
    { unit: "month", seconds: 2_592_000 },
    { unit: "day", seconds: 86_400 },
    { unit: "hour", seconds: 3_600 },
    { unit: "minute", seconds: 60 },
    { unit: "second", seconds: 1 },
  ];
  const unit = units.find(({ seconds }) => Math.abs(elapsedSeconds) >= seconds) ?? units.at(-1)!;
  const value = Math.sign(elapsedSeconds) * Math.floor(Math.abs(elapsedSeconds) / unit.seconds);

  return relativeTimeFormatter.format(value, unit.unit);
};
